import React, { useState } from 'react';
import { Box, Button, Chip, MenuItem, TextField } from '@mui/material';
import { filterOf, labelOf, type FilterDraft } from '../../utils/datasetRowsUtils';
import type { DatasetColumn, EventDatasetFilter } from '../../../../types/backend';

type Match = FilterDraft['match'];

const EMPTY_DRAFT: FilterDraft = { column: '', match: 'equals', value: '', min: '', max: '' };

interface DatasetRowsFilterBarProps {
  /** The columns a filter may name: every column but list and json ones. */
  columns: DatasetColumn[];
  filters: EventDatasetFilter[];
  onChange: (filters: EventDatasetFilter[]) => void;
  /** Right-aligned controls beside the filters. */
  children?: React.ReactNode;
}

/** The server-side filters of a dataset read: text equal to or containing a value, number and time ranges, true or false. */
const DatasetRowsFilterBar: React.FC<DatasetRowsFilterBarProps> = ({ columns, filters, onChange, children }) => {
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const type = columns.find((c) => c.name === draft.column)?.type;
  const filter = type ? filterOf(draft, type) : null;
  const ranged = type === 'number' || type === 'timestamp';
  const set = (patch: Partial<FilterDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
      {filters.map((f, index) => (
        <Chip
          key={`${f.column}-${index}`}
          size="small"
          label={labelOf(f)}
          onDelete={() => onChange(filters.filter((_, i) => i !== index))}
        />
      ))}
      <TextField
        select
        size="small"
        label="Column"
        value={draft.column}
        onChange={(e) => setDraft({ ...EMPTY_DRAFT, column: e.target.value })}
        sx={{ minWidth: 160 }}
      >
        {columns.map((c) => <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>)}
      </TextField>
      {type === 'text' && (
        <TextField
          select
          size="small"
          label="Match"
          value={draft.match}
          onChange={(e) => set({ match: e.target.value as Match })}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="equals">equals</MenuItem>
          <MenuItem value="contains">contains (case-sensitive)</MenuItem>
        </TextField>
      )}
      {type === 'text' && (
        <TextField size="small" label="Value" value={draft.value} onChange={(e) => set({ value: e.target.value })} />
      )}
      {type === 'boolean' && (
        <TextField
          select
          size="small"
          label="Value"
          value={draft.value}
          onChange={(e) => set({ value: e.target.value })}
          sx={{ minWidth: 100 }}
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </TextField>
      )}
      {ranged && (['min', 'max'] as const).map((bound) => (
        <TextField
          key={bound}
          size="small"
          label={bound === 'min' ? 'From' : 'Before'}
          type={type === 'number' ? 'number' : 'datetime-local'}
          value={draft[bound]}
          onChange={(e) => set({ [bound]: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: type === 'number' ? 110 : 210 }}
        />
      ))}
      <Button
        size="small"
        variant="outlined"
        disabled={!filter}
        onClick={() => {
          if (!filter) return;
          onChange([...filters, filter]);
          setDraft(EMPTY_DRAFT);
        }}
      >
        Add filter
      </Button>
      <Box sx={{ flex: 1 }} />
      {children}
    </Box>
  );
};

export default DatasetRowsFilterBar;
